/**
 * LINE LEVEL CATEGORY DECISIONS - the INTERNAL audit surface.
 *
 * Ashok ruling, 2026-08-17: category_decision_source is an internal
 * audit/provenance attribute, not vendor-facing procurement content. It is
 * excluded from every vendor-facing RFP surface (enforced server-side by
 * utils/document_internal_fields.py, both at render time and in CI) and
 * remains available in the internal review/audit record. This component is
 * that record.
 *
 * WHY IT IS NOT RENDERED INSIDE RfpPreview, which is the only other
 * per-line surface on this page. RfpPreview's contract is "this is what
 * the vendor will receive", and a reviewer must be able to trust that
 * without a prop changing what the screen means. It is also shared with
 * the wizard's StepRfpPreview, so a badge there would be buyer-facing copy
 * - a different slice. This component reads final_line_items directly and
 * touches neither.
 *
 * THE LABELS ARE FOR A REVIEWER, NOT A BUYER. They describe the RECORD
 * ("AI proposed, not yet decided"), not the person, and they are not
 * the wizard's vocabulary.
 *
 * NO KEY IS NOT `undetermined`, AND THE DIFFERENCE IS THE POINT. A line
 * with no key predates the provenance slice - 214 line elements in prod
 * and 668 in staging are in exactly that state, permanently, because
 * there is no backfill by ruling. `undetermined` means the field EXISTS
 * and the classifier could not attribute a decision to it. Collapsing the
 * two would defeat the census that counts the first group. So a line with
 * no key renders NO badge and an explicit em-dash placeholder, which is
 * visually and textually distinct from the `undetermined` badge.
 */
import { Info } from 'lucide-react';

// THE FIVE VALUES, as literals. Declared server-side in
// routes/_category_decision_vocabulary.py; restated here rather than
// fetched because this is presentation, and a label is not a value.
export const PROVENANCE_LABELS = {
  accepted_ai: 'AI proposed, accepted',
  user_changed: 'Changed by buyer',
  user_authored: 'Added by buyer',
  ai_proposed: 'AI proposed, not yet decided',
  undetermined: 'No matching proposal',
};

// Muted, audit-surface styling - this is a record, not a status the
// reviewer must act on. `undetermined` is amber because it is the one
// value that says the system could not answer.
const PROVENANCE_STYLES = {
  accepted_ai: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  user_changed: 'bg-blue-50 text-blue-800 border-blue-200',
  user_authored: 'bg-blue-50 text-blue-800 border-blue-200',
  ai_proposed: 'bg-zinc-100 text-zinc-700 border-zinc-200',
  undetermined: 'bg-amber-50 text-amber-900 border-amber-200',
};

export const ProvenanceBadge = ({ value }) => {
  const label = PROVENANCE_LABELS[value];
  // NO KEY -> NO BADGE. An unrecognised value is treated the same way
  // rather than rendered raw: showing an undeclared token to a reviewer
  // would present a defect as a fact.
  if (!label) return null;
  return (
    <span
      className={`inline-block px-2 py-0.5 text-xs border rounded-sm ${PROVENANCE_STYLES[value]}`}
      data-testid={`provenance-badge-${value}`}
    >
      {label}
    </span>
  );
};

const CategoryDecisionsPanel = ({ lineItems }) => {
  const lines = Array.isArray(lineItems) ? lineItems : [];

  if (lines.length === 0) {
    return (
      <div className="bg-white border border-zinc-200 p-12 text-center"
           data-testid="line-provenance-empty">
        <Info className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
        <p className="text-zinc-500">No finalized line items for this procurement.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-zinc-200 p-6" data-testid="line-provenance-panel">
      <h3 className="text-sm font-semibold text-zinc-900 mb-1">
        Line Level Category Decisions
      </h3>
      {/* NO "not shown to vendors" DISCLAIMER (Ashok ruling, 2026-08-17).
          On a pre-slice document every row reads "— not recorded", so a
          disclaimer sitting above them was read as a caption FOR those
          rows - implying something was being withheld on this screen. The
          tab already sits inside an internal-only page; the exclusion is
          enforced server-side (utils/document_internal_fields.py) and
          does not need restating as buyer-visible copy. */}
      <p className="text-xs text-zinc-500 mb-4">
        How each line&apos;s spend category was set.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border border-zinc-200">
          <thead className="bg-zinc-50">
            <tr>
              <th className="py-2 px-2 text-left font-medium text-zinc-700">Line #</th>
              <th className="py-2 px-2 text-left font-medium text-zinc-700">Description</th>
              <th className="py-2 px-2 text-left font-medium text-zinc-700">Category</th>
              <th className="py-2 px-2 text-left font-medium text-zinc-700">Decision source</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((item, index) => {
              const value = item?.category_decision_source;
              const hasBadge = Boolean(PROVENANCE_LABELS[value]);
              return (
                <tr key={`prov-${item?.line_number ?? index}`}
                    className="border-t border-zinc-100"
                    data-testid={`provenance-row-${index}`}>
                  <td className="py-2 px-2 text-zinc-500">{item?.line_number ?? index + 1}</td>
                  <td className="py-2 px-2 text-zinc-700">{item?.description || ''}</td>
                  <td className="py-2 px-2 text-zinc-700">{item?.spend_category || ''}</td>
                  <td className="py-2 px-2">
                    {hasBadge ? (
                      <ProvenanceBadge value={value} />
                    ) : (
                      /* NOT the undetermined badge. A pre-slice line has no
                         record at all, which is a different fact from a
                         record that could not attribute a decision. */
                      <span className="text-zinc-400"
                            data-testid={`provenance-none-${index}`}>
                        — not recorded
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CategoryDecisionsPanel;
